import { KameleoonClient } from '@kameleoon/javascript-sdk';

import { KAMELEOON } from '../config';

let client = null;
let visitorCode = null;
let initializationPromise = null;
let legalConsent = null;

function consentFromGroups(groups) {
  if (typeof groups === 'string') {
    groups = groups.split(',').filter(Boolean);
    if (!groups.length) return null;
  }
  if (!Array.isArray(groups) || groups.some((group) => typeof group !== 'string' || !/^(C|BG)\d+$/.test(group))) {
    return null;
  }
  return groups.includes('C0002');
}

function oneTrustCookie() {
  const cookie = document.cookie.match(/(?:^|;\s*)OptanonConsent=([^;]*)/);
  return cookie ? cookie[1] : '';
}

function consentFromCookie() {
  try {
    const groups = new URLSearchParams(decodeURIComponent(oneTrustCookie())).get('groups');
    const analyticsConsent = groups?.match(/(?:^|,)C0002:([01])(?:,|$)/);
    return analyticsConsent ? analyticsConsent[1] === '1' : null;
  } catch {
    return null;
  }
}

function startupConsent() {
  // Require affirmative analytics consent; OneTrust not loaded yet is not accepted.
  return consentFromGroups(window.OnetrustActiveGroups) ?? consentFromCookie() ?? false;
}

function synchronizeConsent() {
  if (client && visitorCode) client.setLegalConsent(visitorCode, legalConsent);
}

function updateConsent(event) {
  // Events precede the cookie write, so use the reported groups, never reread the cookie.
  const eventConsent = Array.isArray(event.detail) ? consentFromGroups(event.detail) : null;
  const nextConsent = eventConsent ?? consentFromGroups(window.OnetrustActiveGroups);
  if (nextConsent === null || nextConsent === legalConsent) return;

  legalConsent = nextConsent;
  synchronizeConsent();
}

function clearConsent() {
  if (typeof window !== 'undefined') window.removeEventListener('OneTrustGroupsUpdated', updateConsent);
  legalConsent = null;
}

function initializationFailed() {
  clearConsent();
  client = null;
  visitorCode = null;
  return false;
}

function synchronizeVisitorCode(code) {
  if (typeof window === 'undefined' || !code) return;

  window.kameleoonQueue = window.kameleoonQueue || [];
  window.kameleoonQueue.push({
    level: 'IMMEDIATE',
    command: () => window.Kameleoon?.API?.Visitor?.setVisitorCode(code),
  });
}

async function initializeClient() {
  if (!KAMELEOON.siteCode) return false;

  try {
    legalConsent = startupConsent();
    window.addEventListener('OneTrustGroupsUpdated', updateConsent);
    const nextClient = new KameleoonClient({
      siteCode: KAMELEOON.siteCode,
      configuration: {
        updateInterval: 20,
      },
    });
    if ((await nextClient.initialize()) !== true) return initializationFailed();

    const nextVisitorCode = nextClient.getVisitorCode();
    if (!nextVisitorCode) return initializationFailed();

    client = nextClient;
    visitorCode = nextVisitorCode;
    synchronizeConsent();
    synchronizeVisitorCode(visitorCode);
    return true;
  } catch {
    return initializationFailed();
  }
}

export function initialize() {
  if (!initializationPromise) {
    initializationPromise = initializeClient();
  }
  return initializationPromise;
}

function cancelled(isCancelled) {
  return typeof isCancelled === 'function' && isCancelled();
}

function hasEvaluationInput(featureKey, allowlistedVariationKeys, journeyId) {
  return Boolean(featureKey && journeyId && Array.isArray(allowlistedVariationKeys));
}

function canEvaluate(isCancelled) {
  return Boolean(client && visitorCode && !cancelled(isCancelled));
}

function readAllowedVariation(featureKey, allowlistedVariationKeys) {
  const variation = client.getVariation({ visitorCode, featureKey, track: false });
  return variation && allowlistedVariationKeys.includes(variation.key) ? variation : null;
}

function trackVariation(featureKey, variation) {
  const tracked = client.getVariation({ visitorCode, featureKey, track: true });
  return tracked?.key === variation.key ? variation.key : null;
}

function evaluateReadyFeature(featureKey, allowlistedVariationKeys, isCancelled) {
  try {
    const variation = readAllowedVariation(featureKey, allowlistedVariationKeys);
    if (!variation || cancelled(isCancelled)) return null;
    return trackVariation(featureKey, variation);
  } catch {
    return null;
  }
}

export async function evaluateFeature({ featureKey, allowlistedVariationKeys, journeyId, isCancelled }) {
  if (!hasEvaluationInput(featureKey, allowlistedVariationKeys, journeyId)) return null;

  await initialize();
  if (!canEvaluate(isCancelled)) return null;

  return evaluateReadyFeature(featureKey, allowlistedVariationKeys, isCancelled);
}

export async function flushFeatureEvaluation() {
  if (!client || !visitorCode) return false;

  try {
    await client.flushInstant(visitorCode);
    return true;
  } catch {
    return false;
  }
}

export function resetExperimentationForTests() {
  clearConsent();
  client = null;
  visitorCode = null;
  initializationPromise = null;
}
